import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  GoogleMap,
  LoadScript,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./firebase";

const mapContainerStyle = { width: "100%", height: "85vh" };
const GOOGLE_MAPS_API_KEY = "AIzaSyDQJx-KDJUimSyyVUwOBRrrzTG22-rvz18";

const markerIcons = {
  vehicle: "/tracking-image/car_marker.png",
  school: "/tracking-image/school-map-marker.png",
  student: "/tracking-image/student_before_pickup.png",
};
const JAMSHEDPUR_CENTER = { lat: 22.793726, lng: 86.247313 };
const routeColors = ["#f83b2e"];

const isValidCoordinate = (coord) =>
  typeof coord === "number" && !isNaN(coord) && isFinite(coord);

// Helper for safe map size creation
const safeMapSize = (w, h) => {
  if (window.google?.maps?.Size) return new window.google.maps.Size(w, h);
  return undefined;
};

function VehicleDetails() {
  const { driverId } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [directions, setDirections] = useState([]);
  const [center, setCenter] = useState(JAMSHEDPUR_CENTER);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [error, setError] = useState(null);

  const mapRef = useRef(null);
  const vehicleMarkerRef = useRef(null);
  const lastVehiclePos = useRef(null);

  const calculateCenter = useCallback((vehicle, bookings) => {
    if (!vehicle) return JAMSHEDPUR_CENTER;
    const validCoords = [
      vehicle.latitude && vehicle.longitude
        ? { lat: vehicle.latitude, lng: vehicle.longitude }
        : null,
      vehicle.school_lat && vehicle.school_lng
        ? { lat: vehicle.school_lat, lng: vehicle.school_lng }
        : null,
      ...bookings.map((b) =>
        b.lat && b.lng ? { lat: b.lat, lng: b.lng } : null
      ),
    ].filter(
      (coord) => coord && isValidCoordinate(coord.lat) && isValidCoordinate(coord.lng)
    );

    if (!validCoords.length) return JAMSHEDPUR_CENTER;

    const avgLat = validCoords.reduce((sum, c) => sum + c.lat, 0) / validCoords.length;
    const avgLng = validCoords.reduce((sum, c) => sum + c.lng, 0) / validCoords.length;
    return { lat: avgLat, lng: avgLng };
  }, []);

  // Fetch vehicle and bookings
  useEffect(() => {
    const vehicleQuery = query(collection(db, "Users"), where("user", "==", driverId));
    const unsubscribeVehicle = onSnapshot(
      vehicleQuery,
      (snapshot) => {
        const vehicleData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            user: data.user,
            latitude: isValidCoordinate(Number(data.latitude))
              ? Number(data.latitude)
              : null,
            longitude: isValidCoordinate(Number(data.longitude))
              ? Number(data.longitude)
              : null,
            school_lat: isValidCoordinate(Number(data.school_lat))
              ? Number(data.school_lat)
              : null,
            school_lng: isValidCoordinate(Number(data.school_lng))
              ? Number(data.school_lng)
              : null,
          };
        })[0];
        if (vehicleData) {
          setVehicle(vehicleData);
          setError(null);
        } else {
          setError("No vehicle found or missing coordinates.");
        }
      },
      (err) => setError("Failed to fetch vehicle data: " + err.message)
    );

    const bookingsQuery = query(collection(db, "Booking"), where("driver", "==", driverId));
    const unsubscribeBookings = onSnapshot(
      bookingsQuery,
      (snapshot) => {
        const bookingData = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.name,
              lat: isValidCoordinate(Number(data.lat)) ? Number(data.lat) : null,
              lng: isValidCoordinate(Number(data.lng)) ? Number(data.lng) : null,
            };
          })
          .filter((b) => b.lat !== null && b.lng !== null);
        setBookings(bookingData);
      },
      (err) => setError("Failed to fetch student data: " + err.message)
    );

    return () => {
      unsubscribeVehicle();
      unsubscribeBookings();
    };
  }, [driverId]);

  // Smooth vehicle animation
  const moveVehicleSmoothly = useCallback((oldPos, newPos, duration = 2000) => {
    if (!vehicleMarkerRef.current) return;
    const marker = vehicleMarkerRef.current;
    const start = performance.now();

    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const lat = oldPos.lat + (newPos.lat - oldPos.lat) * progress;
      const lng = oldPos.lng + (newPos.lng - oldPos.lng) * progress;
      marker.setPosition({ lat, lng });
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, []);

  // Update marker when vehicle moves
  useEffect(() => {
    if (!vehicle || !vehicleMarkerRef.current) return;

    const newPos = { lat: vehicle.latitude, lng: vehicle.longitude };
    if (lastVehiclePos.current) {
      moveVehicleSmoothly(lastVehiclePos.current, newPos);
    } else {
      vehicleMarkerRef.current.setPosition(newPos);
    }
    lastVehiclePos.current = newPos;
  }, [vehicle, moveVehicleSmoothly]);

  // Compute route (vehicle → students → school)
  const calculateDirections = useCallback(() => {
    if (!window.google || !window.google.maps || !vehicle) return;

    const service = new window.google.maps.DirectionsService();
    const waypoints = bookings.map((b) => ({
      location: { lat: b.lat, lng: b.lng },
      stopover: true,
    }));

    const origin = { lat: vehicle.latitude, lng: vehicle.longitude };
    const destination = vehicle.school_lat
      ? { lat: vehicle.school_lat, lng: vehicle.school_lng }
      : origin;

    service.route(
      {
        origin,
        destination,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: true,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections([{ directions: result }]);
        } else {
          console.error("Directions request failed:", status);
        }
      }
    );
  }, [vehicle, bookings]);

  useEffect(() => {
    if (isMapLoaded && vehicle) {
      setCenter(calculateCenter(vehicle, bookings));
      calculateDirections();
    }
  }, [isMapLoaded, vehicle, bookings, calculateCenter, calculateDirections]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setIsMapLoaded(true);
  }, []);

  return (
    <div style={{ padding: "20px" }}>
      <h1 style={{ fontSize: "24px" }}>Vehicle Details for Driver: {driverId}</h1>
      <Link to="/" style={{ color: "blue", textDecoration: "underline" }}>
        Back to Map
      </Link>

      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={14}
          onLoad={onMapLoad}
        >
          {/* Vehicle Marker */}
          {vehicle && isMapLoaded && window.google?.maps && (
            <Marker
              position={{ lat: vehicle.latitude, lng: vehicle.longitude }}
              title={`Driver: ${vehicle.user}`}
              icon={{
                url: markerIcons.vehicle,
                scaledSize: safeMapSize(70, 30),
              }}
              onLoad={(marker) => (vehicleMarkerRef.current = marker)}
            />
          )}

          {/* School Marker */}
          {vehicle?.school_lat && isMapLoaded && window.google?.maps && (
            <Marker
              position={{ lat: vehicle.school_lat, lng: vehicle.school_lng }}
              title="School"
              icon={{
                url: markerIcons.school,
                scaledSize: safeMapSize(40, 40),
              }}
            />
          )}

          {/* Student Markers */}
          {bookings.map(
            (b) =>
              isMapLoaded &&
              window.google?.maps && (
                <Marker
                  key={b.id}
                  position={{ lat: b.lat, lng: b.lng }}
                  title={b.name}
                  icon={{
                    url: markerIcons.student,
                    scaledSize: safeMapSize(40, 40),
                  }}
                />
              )
          )}

          {/* Route Directions */}
          {directions.map((route, i) => (
            <DirectionsRenderer
              key={i}
              directions={route.directions}
              options={{
                polylineOptions: {
                  strokeColor: routeColors[0],
                  strokeOpacity: 0.8,
                  strokeWeight: 4,
                },
                suppressMarkers: true,
              }}
            />
          ))}
        </GoogleMap>
      </LoadScript>
    </div>
  );
}

export default VehicleDetails;
